import { unref } from "vue";
import { toKebabCase } from "#/shared/utils/string";

const SLOT_TAG_NAME = "SLOT";

/**
 * Returns the first child of the container that is a slot element.
 *
 * @param {Element | SlotElement | import("vue").Ref<Element | SlotElement>} containerOrSlot
 * @returns {SlotElement | undefined}
 */
export function getSlot(containerOrSlot) {
  if (containerOrSlot?.tagName === SLOT_TAG_NAME) {
    return containerOrSlot;
  }

  const firstChild = containerOrSlot?.firstElementChild;

  if (!firstChild || firstChild.tagName !== SLOT_TAG_NAME) {
    return undefined;
  }

  return firstChild;
}

/**
 * Returns all elements in the slot or container.
 *
 * @param {Element | SlotElement | import("vue").Ref<Element | SlotElement>} slotOrContainer
 * @returns {Element[]}
 */
export function getSlotContent(slotOrContainer, { flatten = false } = {}) {
  const slot = getSlot(unref(slotOrContainer));
  if (slot) {
    return slot.assignedElements({ flatten });
  }

  return Array.from(unref(slotOrContainer)?.children ?? []);
}

/**
 * Returns true if the slot or container has any content.
 *
 * @param {Element | SlotElement | import("vue").Ref<Element | SlotElement>} slotOrContainer
 * @param {Object} options
 * @param {boolean} [options.deep=false] If true, will check if the slot or container has any content, including nested slots.
 * @returns {boolean}
 */
export function hasSlotContent(slotOrContainer, { deep = false } = {}) {
  const content = getSlotContent(slotOrContainer, { flatten: true });

  if (content.length === 1 && content[0].tagName === SLOT_TAG_NAME) {
    return hasSlotContent(content[0], { deep });
  }

  return content !== undefined && content.length > 0;
}

/**
 * Returns true if the slot or container has any content, including nested slots
 * Convenience function for `hasSlotContent(slotOrContainer, { deep: true })`.
 *
 * @param {Element | SlotElement | import("vue").Ref<Element | SlotElement>} slotOrContainer
 * @returns {boolean}
 */
export function hasSlotContentDeep(slotOrContainer) {
  return hasSlotContent(slotOrContainer, { deep: true });
}

/**
 * Returns all elements in the slot or container that match the given tag name.
 *
 * @param {Element | SlotElement | import("vue").Ref<Element | SlotElement>} slotOrContainer
 * @param {string} tagName
 * @returns {Element[]}
 */
export function getSlotContentByTagName(slotOrContainer, tagName) {
  return getSlotContent(slotOrContainer).filter((el) => el.tagName === tagName);
}

/**
 * Returns all elements in the slot or container that match the given tag name, including nested slots.
 *
 * @param {Element | SlotElement | import("vue").Ref<Element | SlotElement>} slotOrContainer
 * @param {string} tagName
 * @returns {Element[]}
 */
export function getSlotContentByTagNameDeep(slotOrContainer, tagName) {
  const normalizedTagName = toKebabCase(tagName);

  const content = getSlotContent(slotOrContainer, { flatten: true });
  const querySelectedElements = content.flatMap((el) => Array.from(el.querySelectorAll(normalizedTagName)));
  const querySelectedSlots = content.flatMap((el) => Array.from(el.querySelectorAll(SLOT_TAG_NAME)));

  const slots = querySelectedSlots.concat(content.filter((el) => el.tagName === SLOT_TAG_NAME));
  const elements = querySelectedElements.concat(content.filter((el) => el.tagName.toLowerCase() === normalizedTagName));

  if (slots.length > 0) {
    elements.push(...slots.flatMap((slot) => getSlotContentByTagNameDeep(slot, tagName)));
  }

  return elements;
}
